/**
 * URL conversion utilities for processing markdown content
 * Converts plain HTTP(S) URLs to clickable links while preserving code blocks and existing markdown links
 */

export type ContentBlock = {
    content: string;
    isCodeBlock: boolean;
};

/**
 * Converts plain HTTP(S) URLs in markdown content to clickable links
 * while preserving code blocks and existing markdown links
 *
 * @param content - The markdown content to process
 * @returns Processed content with URLs converted to markdown links
 */
export function convertUrlsToLinks(content: string): string {
    if (!content) return content;

    // Quick check: if no HTTP(S) URLs present, return content unchanged
    // This avoids expensive code block splitting for content without URLs
    if (!/https?:/i.test(content)) {
        return content;
    }

    // Split content into blocks to handle code blocks separately
    const blocks = splitContentIntoBlocks(content);

    // Process each block
    return blocks
        .map((block) => {
            if (block.isCodeBlock) {
                return block.content; // Don't modify code blocks
            }

            return convertPlainUrlsInText(block.content);
        })
        .join("");
}

/**
 * Splits content into code and non-code blocks for selective processing
 *
 * This function uses manual parsing instead of regex to avoid catastrophic backtracking
 * vulnerabilities. It handles both fenced code blocks (```) and inline code blocks (`).
 *
 * @param content - The markdown content to split
 * @returns Array of content blocks with code block identification
 */
export function splitContentIntoBlocks(content: string): ContentBlock[] {
    const blocks: ContentBlock[] = [];
    let pos = 0;

    /**
     * Helper function to add a content block, avoiding empty blocks
     * @param start - Start position in content string
     * @param end - End position in content string
     * @param isCode - Whether this block represents code
     */
    const pushBlock = (start: number, end: number, isCode: boolean) => {
        if (start < end) {
            blocks.push({
                content: content.slice(start, end),
                isCodeBlock: isCode,
            });
        }
    };

    while (pos < content.length) {
        // Find the next backtick character
        const backtick = content.indexOf("`", pos);

        // No more backticks found - add remaining content as regular text
        if (backtick === -1) {
            pushBlock(pos, content.length, false);
            break;
        }

        // Add any non-code text before this backtick
        pushBlock(pos, backtick, false);

        // Determine if this is a fenced code block (```) or inline code (`)
        const isFenced = content.startsWith("```", backtick);

        if (isFenced) {
            // Handle fenced code block: ```code```
            const end = content.indexOf("```", backtick + 3);
            if (end !== -1) {
                // Found closing backticks - create code block
                pushBlock(backtick, end + 3, true);
                pos = end + 3;
            } else {
                // No closing backticks found - treat as regular text
                // This handles malformed markdown gracefully
                pushBlock(backtick, content.length, false);
                break;
            }
        } else {
            // Handle inline code block: `code`
            const end = content.indexOf("`", backtick + 1);
            if (end !== -1) {
                const codeContent = content.slice(backtick + 1, end);

                // Inline code blocks cannot contain newlines (markdown spec)
                if (!codeContent.includes("\n")) {
                    // Valid inline code block
                    pushBlock(backtick, end + 1, true);
                    pos = end + 1;
                } else {
                    // Contains newlines - treat opening backtick as regular text
                    pushBlock(backtick, backtick + 1, false);
                    pos = backtick + 1;
                }
            } else {
                // No closing backtick found - treat as regular text
                pushBlock(backtick, backtick + 1, false);
                pos = backtick + 1;
            }
        }
    }

    return blocks;
}

/**
 * Converts plain URLs in text content to markdown links, avoiding existing markdown links
 *
 * @param text - The text content to process
 * @returns Text with plain URLs converted to markdown links
 */
export function convertPlainUrlsInText(text: string): string {
    const urlRegex = new RegExp(
        [
            // Negative lookbehind: exclude URLs already part of Markdown links like [text](url)
            "(?<!\\]\\()",

            // Match the protocol part: http:// or https://
            // Security: This regex ONLY matches http/https schemes, preventing XSS attacks
            // from malicious schemes like javascript:, data:, vbscript:, etc.
            "(https?:\\/\\/",

            // Match the main part of the URL:
            // - Exclude whitespace
            // - Exclude angle brackets (HTML)
            // - Exclude square and curly brackets (Markdown or custom delimiters)
            "[^\\s<>\\[\\]{}]+?)",

            // Lookahead to ensure the match ends cleanly:
            // - Allow optional trailing punctuation (.!?;,))
            // - Followed by either a space or end of string
            "(?=[.!?;,)]*(?:\\s|$))",
        ].join(""),
        "gi",
    );

    return text.replace(urlRegex, (url) => `[${url}](${url})`);
}
